import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ImageTag from "../components/reuseable/imageTag";
import { getAuth, signOut } from "firebase/auth";

// import { toast } from "react-toastify";
import {
  ChevronLeft,
  ChevronRight,
  X,
  House,
  LogOut,
  LayoutDashboard,
  Users,
  Presentation,
  UsersRound,
  Database,
  Handshake,
  UserCheck,
  ClipboardList,
  Lock,
  RefreshCcw,
  Settings,
  Target,
  Newspaper,
  Bell,
  CalendarDays,
} from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../context/StateProvider";
import { actionType } from "../context/reducer";
import useUnreadFeedCount from "../hooks/useUnreadFeedCount";

// Main scrolling menu — Settings is rendered separately, pinned at the bottom.
const menuItems = [
  {
    title: "Dashboard",
    icon: House,
    paths: ["/dashboard"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Feed",
    icon: Newspaper,
    paths: ["/dashboard/feed"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Deals Board",
    icon: Handshake,
    paths: ["/dashboard/deal-board"],
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
  },
  {
    title: "Transactions",
    icon: LayoutDashboard,
    paths: ["/dashboard/deals"],
    allowedusertypes: ["Admin", "Employee", "Supervisor", "Accountant"],
  },
  {
    title: "Open Houses",
    icon: CalendarDays,
    paths: ["/dashboard/open-houses"],
    allowedusertypes: ["Admin", "Employee", "Supervisor"],
  },
  {
    title: "Contact",
    icon: RefreshCcw,
    paths: ["/dashboard/contact"],
    allowedusertypes: ["Admin", "Supervisor", "Employee"],
  },
  {
    title: "Team Performance",
    icon: Users,
    paths: ["/dashboard/team-management"],
    allowedusertypes: ["Admin", "Accountant"],
  },
  {
    title: "Goals",
    icon: Target,
    paths: ["/dashboard/goals"],
    allowedusertypes: ["Admin", "Employee", "Accountant"],
  },
];

// Pinned at the bottom of the sidebar, above LogOut.
const settingsItem = {
  title: "Settings",
  icon: Settings,
  paths: ["/dashboard/settings"],
  allowedusertypes: ["Admin", "Supervisor", "Employee"],
};

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen, isCollapse }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const location = useLocation();
  const [{ user }, dispatch] = useStateValue();
  const unreadFeed = useUnreadFeedCount();

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          dispatch({
            type: actionType.SET_USER,
            payload: [],
          });
          toast.success("Logged out successfully!");
          navigate("/");
        })
        .catch((error) => {
          alert("sign out error");
        });
    } catch (error) {
      toast.error("Logout failed. Please try again.");
      console.error("Logout Error:", error);
    }
  };

  // ✅ Corrected Active State Logic
  const isActive = (paths) => paths.some((path) => location.pathname === path);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    isCollapse(isOpen);
  };

  const sidebarVariants = {
    open: { width: "20%" },
    closed: { width: "80px", minWidth: "80px" },
  };

  const menuItemVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 },
  };

  const mobileSidebarVariants = {
    open: { y: 0, opacity: 1 },
    closed: { y: "-15%", opacity: 0 },
  };

  return (
    // <AnimatePresence>
    <>
      {(isLargeScreen || isMobileMenuOpen) && (
        <motion.aside
          className={`fixed top-0 left-0 z-40 h-screen w-full bg-gradient-to-b from-[#0f172a] to-[#1E293B] shadow-md flex flex-col ${
            isLargeScreen ? "" : "w-full"
          }`}
          initial={false}
          animate={isLargeScreen ? (isOpen ? "open" : "closed") : "open"}
          exit="closed"
          variants={isLargeScreen ? sidebarVariants : mobileSidebarVariants}
        >
          <div
            className={`flex items-center ${
              isOpen ? "justify-between" : "justify-center"
            } py-4 px-2 border-b border-[#FFFFFF1A] flex-shrink-0`}
          >
            {isOpen && (
              <motion.span
                variants={menuItemVariants}
                className="text-xl font-semibold w-full flex justify-center mt-4"
              >
                <ImageTag
                  path="/assets/logo.png"
                  classes="w-[160px]"
                  altText="login"
                />
              </motion.span>
            )}
            {isLargeScreen && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-full bg-gray-200 hover:bg-gray-200"
              >
                {isOpen ? <ChevronLeft /> : <ChevronRight />}
              </button>
            )}
            {!isLargeScreen && (
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full text-white hover:bg-gray-200"
              >
                <X />
              </button>
            )}
          </div>

          <div className="flex items-center gap-x-3 pl-5 py-3 flex-shrink-0">
            {user?.profilepicture && user?.profilepicture !== "" ? (
              <img
                src={user?.profilepicture}
                alt="user"
                className="size-10 rounded-full object-cover"
              />
            ) : (
              <div className="size-10 rounded-full bg-white flex justify-center items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-[20px]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
              </div>
            )}
            {isOpen && (
              <Link to={"/dashboard/profile"}>
                <p className="text-white font-semibold text-md">
                  {user?.firstname} {user?.lastname}
                </p>
              </Link>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto py-3 space-y-2 min-h-0">
            {(isOpen || !isLargeScreen) && (
              <h3 className="text-textColor px-8 text-md">Main</h3>
            )}
            {menuItems.map(
              (item) =>
                item.allowedusertypes.includes(user?.role) && (
                  <div key={item.title} className="lg:w-full mx-auto">
                    <Link
                      to={item.paths[0]}
                      className={`
      group relative flex items-center font-medium text-slate-300 px-8 py-[0.875rem]
      transition-all duration-300 ease-in-out
      ${
        isActive(item.paths)
          ? "bg-gradient-to-r from-indigo-500/20 to-indigo-500/5 !text-white"
          : "hover:bg-indigo-500/10 hover:text-white hover:pl-9"
      }
    `}
                      onClick={() =>
                        !isLargeScreen && setIsMobileMenuOpen(false)
                      }
                    >
                      <span
                        className={`
        absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md
        bg-gradient-to-b from-indigo-500 to-purple-500
        transition-all duration-300 ease-in-out
        ${isActive(item.paths) ? "h-[70%]" : "h-0 group-hover:h-[70%]"}
      `}
                      ></span>
                      <item.icon className="h-5 w-5 min-w-[20px]" />
                      {(isOpen || !isLargeScreen) && (
                        <motion.span
                          variants={menuItemVariants}
                          className="pl-3"
                        >
                          {item.title}
                        </motion.span>
                      )}
                      {item.title === "Feed" && unreadFeed > 0 && (
                        <>
                          {isOpen || !isLargeScreen ? (
                            <span className="ml-auto flex items-center gap-1 text-rose-100">
                              <Bell className="h-4 w-4" />
                              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
                                {unreadFeed > 9 ? "9+" : unreadFeed}
                              </span>
                            </span>
                          ) : (
                            <span className="absolute top-2 right-3 w-2.5 h-2.5 rounded-full bg-rose-500" />
                          )}
                        </>
                      )}
                    </Link>
                  </div>
                ),
            )}
          </nav>

          <div className="flex-shrink-0 border-t border-[#FFFFFF1A] py-2">
            {settingsItem.allowedusertypes.includes(user?.role) && (
              <Link
                to={settingsItem.paths[0]}
                className={`
      group relative flex items-center font-medium text-slate-300 px-8 py-[0.875rem]
      transition-all duration-300 ease-in-out
      ${
        isActive(settingsItem.paths)
          ? "bg-gradient-to-r from-indigo-500/20 to-indigo-500/5 !text-white"
          : "hover:bg-indigo-500/10 hover:text-white hover:pl-9"
      }
    `}
                onClick={() => !isLargeScreen && setIsMobileMenuOpen(false)}
              >
                <span
                  className={`
        absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md
        bg-gradient-to-b from-indigo-500 to-purple-500
        transition-all duration-300 ease-in-out
        ${
          isActive(settingsItem.paths)
            ? "h-[70%]"
            : "h-0 group-hover:h-[70%]"
        }
      `}
                ></span>
                <settingsItem.icon className="h-5 w-5 min-w-[20px]" />
                {(isOpen || !isLargeScreen) && (
                  <motion.span variants={menuItemVariants} className="pl-3">
                    {settingsItem.title}
                  </motion.span>
                )}
              </Link>
            )}
            <div
              className="group relative flex items-center font-medium text-slate-300 px-8 py-[0.875rem]
      transition-all duration-300 ease-in-out hover:bg-indigo-500/10 hover:text-white hover:pl-9 cursor-pointer"
              onClick={handleLogout}
            >
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-gradient-to-b from-indigo-500 to-purple-500 transition-all duration-300 ease-in-out h-0 group-hover:h-[70%]"></span>
              <LogOut className="h-5 w-5 min-w-[20px]" />
              {isOpen && (
                <motion.span variants={menuItemVariants} className="pl-3">
                  LogOut
                </motion.span>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </>
    // </AnimatePresence>
  );
};

export default Sidebar;
